<template lang="pug">
v-card.suus__attend__action-card(min-height="300")
  v-container.pa-8(fluid)
    v-row
      v-col(cols="10")
        v-skeleton-loader.mt-4.mb-6(
          v-if="loading",
          type="heading",
          :loading="true"
        ) 
        Header(
          v-else,
          :text="title",
          :level="eventTitleLevel",
          customClass="mb-4"
        )
      transition(name="fade")
        v-col.d-flex.justify-end(v-if="icon", cols="2")
          StatusIcon(:icon="icon")
    v-row(no-gutters, align="start")
      v-col(cols="12", sm="7", v-if="showEventDetails")
        v-row(v-if="loading")
          v-col(cols="12")
            v-skeleton-loader.mt-4.mb-8(
              v-for="el in 2",
              :key="el",
              type="paragraph",
              :loading="true"
            )
        v-row(align="center", v-else) 
          v-col(cols="3", sm="2") 
            v-icon(color="textColor", size="40") {{ calenderIcon }}
          v-col(cols="9", sm="10")
            Subtitle(
              text="Datum",
              :fontSize="1.2",
              customClass="mb-1",
              :fontWeight="900"
            )
            TextBody.mb-0
              span(v-if="eventStartDate") {{ eventStartDate | formatDate }}
              span(v-if="eventEndDate") {{ dateSeparator }}
              span(v-if="eventEndDate") {{ eventEndDate | formatDate }}
            Hyperlink(
              text="Zur Veranstaltung für mehr Details",
              :url="eventUrl"
            )
          v-col(cols="3", sm="2", v-if="location")
            v-icon(color="textColor", size="40") {{ placeIcon }}
          v-col(cols="9", sm="10", v-if="location")
            Subtitle(
              text="Ort",
              :fontSize="1.2",
              customClass="mb-1",
              :fontWeight="900"
            )
            TextBody(:text="location", customClass="mb-0")
            Hyperlink(
              text="In Google Maps öffnen",
              :url="location | googleMaps",
              targetBlank,
              v-if="location"
            )
      v-col.pr-sm-8(cols="12", sm="7", v-else)
        v-skeleton-loader(type="paragraph", :loading="true", v-if="loading")
        TextBody(
          :text="confirmationMessage",
          customClass="mb-0",
          large,
          v-if="!loading && confirmationMessage"
        )
      v-col(cols="12", sm="5", :class="confettiFlagClasses")
        ConfettiFlag(:width="graphicWidth")
    v-row(align="end", justify-sm="end")
      v-col.py-2(
        cols="12",
        sm="6",
        md="5",
        offset-sm="0",
        offset-md="1",
        v-if="secondaryBtnLabel"
      )
        .text-center
          v-dialog(:width="modalWidth", v-model="showModal")
            template(v-slot:activator="{ on, attrs }")
              TextButton(
                :label="secondaryBtnLabel",
                color="textColor",
                block,
                large,
                v-bind="attrs",
                v-on="on"
              )
            GuestModal(:guest_count="ownUserGuestsCount", @close="showModal = false")
      v-col.order-first.order-sm-last.py-2.mt-4.mt-md-0(cols="12", :sm="6")
        Button(
          v-if="primaryBtnLabel",
          @click="primaryClick",
          block,
          :btnLabel="primaryBtnLabel"
        )
</template>
<script lang="ts">
import { createNamespacedHelpers } from "vuex"
const { mapState } = createNamespacedHelpers("event")
import { EVENT_STATUS, EVENT_URL } from "constants/constants"
import { mdiCheck } from "@mdi/js"
import { mdiClose } from "@mdi/js"
import { mdiCalendarRange } from "@mdi/js"
import { mdiMapMarkerOutline } from "@mdi/js"
import ConfettiFlag from "../graphics/ConfettiFlag.vue"
import Subtitle from "../UI/Subtitle.vue"
import Header from "../UI/Header.vue"
import Button from "../UI/Button.vue"
import TextButton from "../UI/TextButton.vue"
import TextBody from "../UI/TextBody.vue"
import Hyperlink from "../UI/Hyperlink.vue"
import StatusIcon from "../UI/StatusIcon.vue"
import GuestModal from "./GuestModal.vue"
import { Event } from "suus-api"
import { defineComponent } from "vue"
import { useOwnUserAttendanceContext } from "app/modules/events/use-own-user-attendance-context.concern"

interface CardStatus {
  primaryBtnLabel?: string
  secondaryBtnLabel?: string
  icon?: string
}

type EventCardOptions = { [key: string]: CardStatus }

export default defineComponent({
  name: "AttendCard",
  components: {
    ConfettiFlag,
    Header,
    TextButton,
    StatusIcon,
    Hyperlink,
    TextBody,
    Subtitle,
    Button,
    GuestModal,
  },
  data: function () {
    return {
      showModal: false,
      calenderIcon: mdiCalendarRange,
      placeIcon: mdiMapMarkerOutline,
      dateSeparator: " - ",
    }
  },
  methods: {
    primaryClickAttend(): void {
      if (
        this.cardStatus === EVENT_STATUS.REGISTER ||
        this.cardStatus === EVENT_STATUS.DEREGISTERED
      ) {
        this.attendFromDigest()
      } else if (
        this.cardStatus === EVENT_STATUS.REGISTERED ||
        this.cardStatus === EVENT_STATUS.FINISHED_REGISTRATION
      ) {
        window.open(this.eventUrl ?? undefined, "_self")
      } else if (this.cardStatus === EVENT_STATUS.ATTEND_ONLINE_NOW) {
        window.open(this.eventData.custom_call_url ?? undefined, "_self")
      }
    },
    primaryClickDecline(): void {
      if (this.cardStatus === EVENT_STATUS.DEREGISTERED) {
        window.open(this.eventUrl ?? undefined, "_self")
      } else {
        this.declineFromDigest()
      }
    },
    primaryClick(): void {
      return this.$route.name === "Attend"
        ? this.primaryClickAttend()
        : this.primaryClickDecline()
    },
  },
  computed: {
    cardAttendOptions(): EventCardOptions {
      return {
        register: {
          primaryBtnLabel: this.$t(
            "components.attend_card.card_options.register.primaryBtnLabel"
          ).toString(),
        },
        registered: {
          primaryBtnLabel: this.$t(
            "components.attend_card.card_options.registered.primaryBtnLabel"
          ).toString(),
          secondaryBtnLabel: this.$t(
            "components.attend_card.card_options.registered.secondaryBtnLabel"
          ).toString(),
          icon: mdiCheck,
        },
        deregistered: {
          primaryBtnLabel: this.$t(
            "components.attend_card.card_options.register.primaryBtnLabel"
          ).toString(),
        },
        finished_registration: {
          primaryBtnLabel: this.$t(
            "components.attend_card.card_options.finished_registration.primaryBtnLabel"
          ).toString(),
          secondaryBtnLabel: this.$t(
            "components.attend_card.card_options.finished_registration.secondaryBtnLabel"
          ).toString(),
          icon: mdiCheck,
        },
        attend_online_now: {
          primaryBtnLabel: this.$t(
            "components.attend_card.card_options.attend_online_now.primaryBtnLabel"
          ).toString(),
          icon: mdiCheck,
        },
        event_passed: {},
      }
    },
    cardDeclineOptions(): EventCardOptions {
      return {
        register: {
          primaryBtnLabel: this.$t("declines.decline").toString(),
        },
        deregistered: {
          primaryBtnLabel: "Zur Veranstaltung",
          icon: mdiClose,
        },
        registered: {
          primaryBtnLabel: this.$t("declines.decline").toString(),
        },
        finished_registration: {
          primaryBtnLabel: this.$t("declines.decline").toString(),
        },
        attend_online_now: {
          primaryBtnLabel: this.$t("declines.decline").toString(),
        },
        event_passed: {},
      }
    },
    cardOptions(): EventCardOptions {
      return this.$route.name === "Attend"
        ? this.cardAttendOptions
        : this.cardDeclineOptions
    },
    loading(): boolean {
      return this.eventLoading || this.ownUserStatusLoading
    },
    eventId(): number {
      return parseInt(this.$route.params.event_id)
    },
    title(): string {
      return this.eventData && this.eventData.title
    },
    location(): string | null {
      return this.eventData && this.eventData.location
    },
    showEventDetails(): boolean {
      return (
        this.$route.name === "Decline" ||
        this.cardStatus === EVENT_STATUS.REGISTER ||
        this.cardStatus === EVENT_STATUS.DEREGISTERED ||
        this.cardStatus === EVENT_STATUS.EVENT_PASSED ||
        this.cardStatus === EVENT_STATUS.ATTEND_ONLINE_NOW
      )
    },
    confettiFlagClasses(): string[] {
      return [
        `suus__grapic d-none d-sm-flex mb-4`,
        `${
          (this.cardStatus === EVENT_STATUS.EVENT_PASSED ||
            this.cardStatus === EVENT_STATUS.DEREGISTERED) &&
          "disabled"
        }`,
      ]
    },
    primaryBtnLabel(): string | null {
      if (this.cardOptions[this.cardStatus].primaryBtnLabel) {
        return this.cardOptions[this.cardStatus].primaryBtnLabel || null
      }
      return null
    },
    secondaryBtnLabel(): string | null {
      if (
        this.cardOptions[this.cardStatus].secondaryBtnLabel &&
        this.allow_guests
      ) {
        return this.cardOptions[this.cardStatus].secondaryBtnLabel || null
      }
      return null
    },
    icon(): string | null {
      if (this.cardOptions[this.cardStatus].icon) {
        return this.cardOptions[this.cardStatus].icon || null
      }
      return null
    },
    confirmationMessage(): string | null {
      if (this.cardStatus === EVENT_STATUS.REGISTERED && this.allow_guests) {
        return this.$i18n
          .t("components.confirmationMessage.REGISTERED", [this.ownUserName])
          .toString()
      }
      if (
        this.cardStatus === EVENT_STATUS.FINISHED_REGISTRATION &&
        !this.allow_guests
      ) {
        return this.$i18n
          .t("components.confirmationMessage.NO_GUESTS_ALLOWED", [
            this.ownUserName,
          ])
          .toString()
      }
      if (
        this.cardStatus === EVENT_STATUS.FINISHED_REGISTRATION &&
        this.allow_guests
      ) {
        return this.ownUserGuestsCount === 1
          ? this.$i18n
              .t("components.confirmationMessage.GUESTS_ALLOWED.one", [
                this.ownUserName,
              ])
              .toString()
          : this.$i18n
              .t("components.confirmationMessage.GUESTS_ALLOWED.more", [
                this.ownUserName,
                this.ownUserGuestsCount,
              ])
              .toString()
      }
      return null
    },
    eventTitleLevel(): number {
      return this.$vuetify.breakpoint.name === "xs" ? 4 : 1
    },
    graphicWidth(): string {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return "180"
        case "sm":
          return "100%"
        default:
          return "250"
      }
    },
    modalWidth(): number {
      // TODO: Refactor into mixin, also used in EventAttendeeList
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return 600
        case "sm":
          return 800
        case "md":
          return 600
        default:
          return 800
      }
    },
    eventUrl(): string | null {
      return this.eventData ? `${EVENT_URL}${this.eventData.id}` : null
    },
    showEndDate(): boolean {
      if (
        this.eventData &&
        this.eventData.end_at &&
        this.eventData.end_at !== this.eventData.start_at
      ) {
        return true
      }
      return false
    },
    eventStartDate(): string | null {
      if (this.eventData && this.eventData.start_at) {
        return this.eventData.start_at
      }
      return null
    },
    eventEndDate(): string | null {
      if (this.showEndDate && this.eventData && this.eventData.end_at) {
        return this.eventData.end_at
      }
      return null
    },
    ...mapState({
      eventData: (state: any) => state.currentEvent.data as Event,
      eventLoading: (state: any) => state.currentEvent.loading as boolean,
      allow_guests: (state: any) =>
        (state.currentEvent.data &&
          state.currentEvent.data.allow_guests) as boolean,
    }),
  },
  setup() {
    const {
      eventAttendStatus,
      attendFromDigest,
      declineFromDigest,
      ownUserGuestsCount,
      ownUserStatusLoading,
      ownUserName,
    } = useOwnUserAttendanceContext()
    return {
      cardStatus: eventAttendStatus,
      attendFromDigest,
      declineFromDigest,
      ownUserGuestsCount,
      ownUserStatusLoading,
      ownUserName,
    }
  },
})
</script>

<style lang="sass">
.fade-enter-active, .fade-leave-active
  transition: all .5s cubic-bezier(.47,1.64,.41,.8)
.fade-enter, .fade-leave-to
  opacity: 0
  transform: scale(1.1)
.suus__grapic
  &.disabled
    opacity: 0.3
</style>
